import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useWedding } from '../contexts/WeddingContext';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectGroup,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Plus, Edit, Trash, Calendar as CalendarIcon, User, Settings, Lock, LockSlash } from 'iconoir-react';
import { Slider } from '../components/ui/slider';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CUSTOM_OPTION = 'Custom';

export default function EventsPage() {
  const { 
    currentWedding, events, expenses, guests,
    fetchEvents, createEvent, updateEvent, deleteEvent, fetchExpenses, fetchGuests
  } = useWedding();
  
  const [showModal, setShowModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    event_date: '',
    budget: '',
    description: ''
  });
  const [selectedEventType, setSelectedEventType] = useState('');
  const [catalog, setCatalog] = useState([]);

  // Inline-slider state: which event card has its rebalance slider open, and
  // the draft value being dragged (so the parent re-render doesn't fight the
  // slider thumb's position). On commit we persist via updateEvent.
  const [sliderOpen, setSliderOpen] = useState(null);     // event.id or null
  const [sliderDraft, setSliderDraft] = useState(null);   // numeric draft

  // Auto-balance modal — shown when the user clicks "Auto-balance" in the
  // over-allocation pill. Confirms before mutating every event's budget.
  const [showAutoBalance, setShowAutoBalance] = useState(false);
  const [autoBalancing, setAutoBalancing] = useState(false);

  useEffect(() => {
    if (currentWedding) {
      fetchEvents(currentWedding.id);
      fetchExpenses(currentWedding.id);
      fetchGuests(currentWedding.id);
    }
  }, [currentWedding, fetchEvents, fetchExpenses, fetchGuests]);

  useEffect(() => {
    let active = true;
    axios.get(`${API}/event-catalog`, { withCredentials: true })
      .then(({ data }) => { if (active) setCatalog(data.events || []); })
      .catch(() => { /* fall back to Custom-only selection */ });
    return () => { active = false; };
  }, []);

  // Group catalog entries by primary culture for the select dropdown
  const catalogByCulture = catalog.reduce((acc, e) => {
    const culture = (e.cultures && e.cultures[0]) || 'Other';
    if (!acc[culture]) acc[culture] = [];
    acc[culture].push(e);
    return acc;
  }, {});
  // Stable, presentable order
  const CULTURE_ORDER = [
    'North Indian', 'South Indian', 'Punjabi Sikh', 'Pakistani',
    'Bengali', 'Gujarati / Marwari', 'Modern / Western', 'Other'
  ];
  const orderedCultures = CULTURE_ORDER.filter((c) => catalogByCulture[c]);

  const handleOpenModal = (event = null) => {
    if (event) {
      setEditingEvent(event);
      setFormData({
        name: event.name,
        event_date: event.event_date || '',
        budget: event.budget?.toString() || '',
        description: event.description || ''
      });
      const inCatalog = catalog.some((c) => c.name === event.name);
      setSelectedEventType(inCatalog ? event.name : CUSTOM_OPTION);
    } else {
      setEditingEvent(null);
      setFormData({ name: '', event_date: '', budget: '', description: '' });
      setSelectedEventType('');
    }
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const eventName = selectedEventType === CUSTOM_OPTION ? formData.name : selectedEventType;
    const eventData = {
      wedding_id: currentWedding.id,
      name: eventName,
      event_date: formData.event_date || null,
      budget: parseFloat(formData.budget) || 0,
      description: formData.description
    };

    if (editingEvent) {
      await updateEvent(editingEvent.id, eventData);
    } else {
      const created = await createEvent(eventData);
      const added = created?.checklist_items_added;
      if (added && added > 0) {
        toast.success(`Added ${eventName} + ${added} checklist tasks`);
      }
    }
    setShowModal(false);
    fetchEvents(currentWedding.id);
  };

  const handleDelete = async (eventId) => {
    if (window.confirm('Are you sure you want to delete this event?')) {
      await deleteEvent(eventId);
    }
  };

  const getEventSpent = (eventId) => {
    return expenses
      .filter(e => e.event_id === eventId)
      .reduce((sum, e) => sum + (e.amount || 0), 0);
  };

  // Count of guests invited to an event = guests whose `events_invited`
  // includes this event's name (matches storage convention used by RSVP +
  // the guest add/edit modal) plus their plus-ones.
  const getEventGuestCount = (eventName) => {
    if (!eventName || !guests || guests.length === 0) {
      return { headcount: 0, primary: 0 };
    }
    let primary = 0;
    let headcount = 0;
    for (const g of guests) {
      const invited = Array.isArray(g.events_invited) ? g.events_invited : [];
      if (invited.includes(eventName)) {
        primary += 1;
        headcount += 1 + (parseInt(g.plus_ones, 10) || 0);
      }
    }
    return { headcount, primary };
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currentWedding?.preferred_currency || 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  // Sum of every event's budget — drives the "Total committed" indicator
  // beneath the page heading. We compare this against the wedding-level
  // `total_budget` so couples instantly see if rebalancing has pushed
  // their allocations above 100% (turns terracotta) or if they still
  // have headroom (moss).
  const totalEventBudgets = (events || []).reduce(
    (sum, e) => sum + (Number(e.budget) || 0),
    0,
  );
  const weddingTotal = Number(currentWedding?.total_budget) || 0;
  const allocationPct = weddingTotal > 0
    ? Math.round((totalEventBudgets / weddingTotal) * 100)
    : 0;
  const overAllocated = weddingTotal > 0 && totalEventBudgets > weddingTotal;

  // Auto-balance: scale every UNLOCKED event's budget proportionally so the
  // overall sum equals the wedding total. Locked events keep their current
  // budget — the unlocked pool absorbs the entire deficit/surplus. Round
  // each new budget to the nearest dollar and absorb any rounding remainder
  // onto the largest unlocked event so the sum lands exactly on
  // `weddingTotal` (no $1 drift). Each updateEvent call is awaited
  // sequentially — events lists are small (<20) so this is fine.
  const handleAutoBalance = async () => {
    if (!overAllocated) return;
    const lockedTotal = events
      .filter((e) => e.budget_locked)
      .reduce((s, e) => s + (Number(e.budget) || 0), 0);
    const unlocked = events.filter((e) => !e.budget_locked);
    const unlockedSum = unlocked.reduce((s, e) => s + (Number(e.budget) || 0), 0);
    const targetForUnlocked = weddingTotal - lockedTotal;

    // Edge case: locked events already meet/exceed the wedding total — we
    // can't bring it down without unlocking something. Bail out with a
    // helpful toast instead of zeroing the unlocked events.
    if (targetForUnlocked < 0) {
      toast.error(
        `Locked events already total ${formatCurrency(lockedTotal)} — unlock one to auto-balance.`
      );
      setShowAutoBalance(false);
      return;
    }
    if (unlocked.length === 0) {
      toast.error('All events are locked — unlock at least one to auto-balance.');
      setShowAutoBalance(false);
      return;
    }
    if (unlockedSum <= 0) {
      // Nothing to scale; distribute targetForUnlocked evenly.
      const perEach = Math.floor(targetForUnlocked / unlocked.length);
      const drift = targetForUnlocked - perEach * unlocked.length;
      const planned = unlocked.map((e, i) => ({
        event: e,
        newBudget: perEach + (i === 0 ? drift : 0),
      }));
      setAutoBalancing(true);
      try {
        for (const { event, newBudget } of planned) {
          await updateEvent(event.id, { ...event, budget: newBudget });
        }
        toast.success(`Balanced ${planned.length} unlocked events to fit ${formatCurrency(weddingTotal)}`);
        setShowAutoBalance(false);
      } finally {
        setAutoBalancing(false);
      }
      return;
    }

    setAutoBalancing(true);
    try {
      const factor = targetForUnlocked / unlockedSum;
      const planned = unlocked.map((e) => ({
        event: e,
        newBudget: Math.max(0, Math.round((Number(e.budget) || 0) * factor)),
      }));
      const sumPlanned = planned.reduce((s, p) => s + p.newBudget, 0);
      const drift = targetForUnlocked - sumPlanned;
      if (drift !== 0 && planned.length > 0) {
        planned.sort((a, b) => b.newBudget - a.newBudget);
        planned[0].newBudget = Math.max(0, planned[0].newBudget + drift);
      }
      for (const { event, newBudget } of planned) {
        await updateEvent(event.id, { ...event, budget: newBudget });
      }
      const lockedCount = events.length - unlocked.length;
      toast.success(
        lockedCount > 0
          ? `Balanced ${planned.length} unlocked events (${lockedCount} locked) to fit ${formatCurrency(weddingTotal)}`
          : `Balanced ${planned.length} events to fit ${formatCurrency(weddingTotal)}`
      );
      setShowAutoBalance(false);
    } catch (err) {
      console.error('Auto-balance failed', err);
      toast.error('Could not auto-balance — please try again');
    } finally {
      setAutoBalancing(false);
    }
  };

  // Toggle the budget_locked flag for a single event.
  const handleToggleLock = async (event) => {
    try {
      const next = !event.budget_locked;
      await updateEvent(event.id, { ...event, budget_locked: next });
      toast.success(next ? `${event.name} budget locked` : `${event.name} budget unlocked`);
    } catch (err) {
      console.error('Toggle lock failed', err);
      toast.error('Could not save — please try again');
    }
  };

  if (!currentWedding) {
    return (
      <Layout>
        <div className="text-center py-12 text-muted">
          Please select or create a wedding first.
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8" data-testid="events-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-serif font-light text-4xl sm:text-5xl text-charcoal tracking-tight">
              Events
            </h1>
            <p className="text-muted font-light mt-2">
              Manage your wedding events and their budgets
            </p>
            {weddingTotal > 0 && events.length > 0 && (
              <div
                className={`inline-flex items-center gap-2 mt-3 px-3 py-1.5 rounded-full border text-[11px] font-mono tracking-wider uppercase transition-colors ${
                  overAllocated
                    ? 'border-terracotta/40 bg-terracotta/10 text-terracotta'
                    : allocationPct >= 95
                      ? 'border-ochre/40 bg-ochre/10 text-ochre'
                      : 'border-moss/30 bg-moss/8 text-moss'
                }`}
                data-testid="total-allocation-indicator"
              >
                <span
                  className={`w-1.5 h-1.5 rounded-full ${
                    overAllocated ? 'bg-terracotta' : allocationPct >= 95 ? 'bg-ochre' : 'bg-moss'
                  }`}
                />
                <span>
                  Total committed:{' '}
                  <span className="text-charcoal/80">{formatCurrency(totalEventBudgets)}</span>
                  {' '}· {allocationPct}% of {formatCurrency(weddingTotal)}
                </span>
                {overAllocated && (
                  <>
                    <span
                      className="text-terracotta normal-case font-sans font-medium tracking-normal"
                      data-testid="over-allocation-warning"
                    >
                      — over by {formatCurrency(totalEventBudgets - weddingTotal)}
                    </span>
                    <button
                      type="button"
                      onClick={() => setShowAutoBalance(true)}
                      className="ml-2 px-2.5 py-0.5 rounded-full bg-terracotta text-white text-[10px] tracking-wider uppercase hover:bg-terracotta-dark transition-colors normal-case font-sans font-medium"
                      data-testid="auto-balance-btn"
                    >
                      Auto-balance
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
          <Button 
            onClick={() => handleOpenModal()}
            className="btn-primary flex items-center gap-2"
            data-testid="add-event-btn"
          >
            <Plus className="w-4 h-4" />
            Add Event
          </Button>
        </div>

        {/* Events Grid */}
        {events.length === 0 ? (
          <Card className="card-surface p-12 text-center">
            <p className="text-muted mb-4">No events created yet</p>
            <Button 
              onClick={() => handleOpenModal()}
              className="btn-primary"
              data-testid="create-first-event-btn"
            >
              Create Your First Event
            </Button>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {events.map((event) => {
              const spent = getEventSpent(event.id);
              const progress = event.budget ? (spent / event.budget) * 100 : 0;
              const isOverBudget = progress > 100;
              const { headcount, primary } = getEventGuestCount(event.name);

              return (
                <Card
                  key={event.id}
                  className={`card-surface p-6 relative ${event.budget_locked ? 'ring-1 ring-terracotta/20' : ''}`}
                  data-testid={`event-card-${event.id}`}
                >
                  {event.budget_locked && (
                    <div
                      className="absolute -top-2 left-4 inline-flex items-center gap-1 px-2 py-0.5 bg-terracotta text-white text-[9px] font-mono uppercase tracking-wider rounded-sm"
                      data-testid={`locked-badge-${event.id}`}
                    >
                      <Lock className="w-2.5 h-2.5" strokeWidth={2} /> Locked
                    </div>
                  )}
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="font-serif text-xl text-charcoal">{event.name}</h3>
                      {event.event_date && (
                        <p className="text-sm text-muted flex items-center gap-1 mt-1">
                          <CalendarIcon className="w-3 h-3" />
                          {new Date(event.event_date).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          })}
                        </p>
                      )}
                      <p
                        className="text-sm text-muted flex items-center gap-1 mt-1"
                        data-testid={`event-guest-count-${event.id}`}
                      >
                        <User className="w-3 h-3" strokeWidth={1.5} />
                        {primary === 0 ? (
                          <span>No guests invited yet</span>
                        ) : (
                          <span>
                            <span className="font-mono text-charcoal mr-1">{headcount}</span>
                            guest{headcount === 1 ? '' : 's'} invited
                            {headcount !== primary && (
                              <span className="text-xs text-muted ml-1">
                                ({primary} + {headcount - primary} plus-one{headcount - primary === 1 ? '' : 's'})
                              </span>
                            )}
                          </span>
                        )}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleToggleLock(event)}
                        className={`p-2 transition-colors ${
                          event.budget_locked
                            ? 'text-terracotta'
                            : 'text-muted hover:text-charcoal'
                        }`}
                        data-testid={`lock-event-${event.id}`}
                        aria-label={event.budget_locked ? 'Unlock budget' : 'Lock budget'}
                        title={event.budget_locked ? 'Budget locked — unlock to rebalance' : 'Lock budget (excluded from auto-balance)'}
                      >
                        {event.budget_locked ? (
                          <Lock className="w-4 h-4" strokeWidth={1.5} />
                        ) : (
                          <LockSlash className="w-4 h-4" strokeWidth={1.5} />
                        )}
                      </button>
                      <button
                        onClick={() => {
                          if (event.budget_locked) {
                            toast.message('Budget is locked — unlock to rebalance');
                            return;
                          }
                          setSliderOpen(sliderOpen === event.id ? null : event.id);
                          setSliderDraft(event.budget || 0);
                        }}
                        className={`p-2 transition-colors ${
                          event.budget_locked
                            ? 'text-muted/40 cursor-not-allowed'
                            : sliderOpen === event.id
                              ? 'text-terracotta'
                              : 'text-muted hover:text-charcoal'
                        }`}
                        data-testid={`rebalance-event-${event.id}`}
                        aria-label="Rebalance budget"
                        title={event.budget_locked ? 'Budget locked' : 'Rebalance budget'}
                      >
                        <Settings className="w-4 h-4" strokeWidth={1.5} />
                      </button>
                      <button
                        onClick={() => handleOpenModal(event)}
                        className="p-2 text-muted hover:text-charcoal transition-colors"
                        data-testid={`edit-event-${event.id}`}
                      >
                        <Edit className="w-4 h-4" strokeWidth={1.5} />
                      </button>
                      <button
                        onClick={() => handleDelete(event.id)}
                        className="p-2 text-muted hover:text-terracotta transition-colors"
                        data-testid={`delete-event-${event.id}`}
                      >
                        <Trash className="w-4 h-4" strokeWidth={1.5} />
                      </button>
                    </div>
                  </div>

                  {event.description && (
                    <p className="text-sm text-muted mb-4 line-clamp-2">{event.description}</p>
                  )}

                  <div className="space-y-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted">Budget</span>
                      <span className="font-mono text-charcoal">{formatCurrency(event.budget)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted">Spent</span>
                      <span className={`font-mono ${isOverBudget ? 'text-terracotta' : 'text-charcoal'}`}>
                        {formatCurrency(spent)}
                      </span>
                    </div>
                    <Progress 
                      value={Math.min(progress, 100)} 
                      className={`h-2 ${isOverBudget ? '[&>div]:bg-terracotta' : ''}`}
                    />
                    <div className="flex justify-between text-xs">
                      <span className={isOverBudget ? 'text-terracotta' : 'text-moss'}>
                        {isOverBudget ? 'Over budget' : `${formatCurrency(event.budget - spent)} remaining`}
                      </span>
                      <span className="font-mono text-muted">{progress.toFixed(0)}%</span>
                    </div>

                    {/* Inline rebalance slider — opens on rebalance button tap.
                        Drafts the new budget locally as you drag; commits via
                        updateEvent on release so we don't spam the API. */}
                    {sliderOpen === event.id && currentWedding?.total_budget > 0 && (() => {
                      const total = currentWedding.total_budget;
                      const draft = sliderDraft ?? event.budget ?? 0;
                      const max = Math.max(total * 0.8, draft);
                      const step = Math.max(50, Math.round(total / 200));
                      const pct = Math.round((draft / total) * 100);
                      return (
                        <div
                          className="pt-3 mt-1 border-t border-border space-y-2"
                          data-testid={`rebalance-panel-${event.id}`}
                        >
                          <div className="flex items-baseline justify-between">
                            <span className="text-[10px] font-mono uppercase tracking-wider text-terracotta">
                              Rebalance
                            </span>
                            <span className="font-mono text-sm text-charcoal" data-testid={`rebalance-amount-${event.id}`}>
                              {formatCurrency(draft)} <span className="text-muted text-[10px] ml-1">{pct}% of total</span>
                            </span>
                          </div>
                          <Slider
                            value={[draft]}
                            onValueChange={(vals) => setSliderDraft(Math.round(vals[0]))}
                            onValueCommit={async (vals) => {
                              const newBudget = Math.round(vals[0]);
                              try {
                                await updateEvent(event.id, { ...event, budget: newBudget });
                                toast.success(`${event.name} budget set to ${formatCurrency(newBudget)}`);
                              } catch {
                                toast.error('Could not save — please try again');
                              }
                            }}
                            min={0}
                            max={max}
                            step={step}
                            data-testid={`rebalance-slider-${event.id}`}
                          />
                          <div className="flex items-center justify-between text-[10px] font-mono uppercase tracking-wider text-muted">
                            <span>0</span>
                            <button
                              type="button"
                              onClick={() => { setSliderOpen(null); setSliderDraft(null); }}
                              className="hover:text-charcoal transition-colors"
                            >
                              Done
                            </button>
                            <span>{formatCurrency(max)}</span>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </Card>
              );
            })}
          </div>
        )}

        {/* Add/Edit Modal */}
        <Dialog open={showModal} onOpenChange={setShowModal}>
          <DialogContent className="bg-white max-w-md">
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl">
                {editingEvent ? 'Edit Event' : 'Add New Event'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-5 mt-4">
              <div className="space-y-2">
                <Label className="text-charcoal font-sans text-sm uppercase tracking-wider">Event Type</Label>
                <Select value={selectedEventType} onValueChange={setSelectedEventType}>
                  <SelectTrigger className="input-field h-12" data-testid="event-type-select">
                    <SelectValue placeholder="Select event type (any culture)" />
                  </SelectTrigger>
                  <SelectContent className="max-h-80">
                    {orderedCultures.map((culture) => (
                      <SelectGroup key={culture}>
                        <SelectLabel className="text-[10px] tracking-[0.2em] uppercase text-muted font-mono px-2 pt-2">
                          {culture}
                        </SelectLabel>
                        {catalogByCulture[culture].map((e) => (
                          <SelectItem key={e.name} value={e.name} data-testid={`event-opt-${e.name.replace(/[^a-zA-Z0-9]/g, '-')}`}>
                            <span>{e.name}</span>
                            {e.checklist_count > 0 && (
                              <span className="ml-2 text-[10px] text-muted font-mono">+{e.checklist_count} tasks</span>
                            )}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    ))}
                    <SelectGroup>
                      <SelectLabel className="text-[10px] tracking-[0.2em] uppercase text-muted font-mono px-2 pt-2">
                        Other
                      </SelectLabel>
                      <SelectItem value={CUSTOM_OPTION} data-testid="event-opt-custom">Custom (name it yourself)</SelectItem>
                    </SelectGroup>
                  </SelectContent>
                </Select>
                {selectedEventType && selectedEventType !== CUSTOM_OPTION && !editingEvent && (
                  <p className="text-[11px] text-moss font-light" data-testid="event-type-hint">
                    Adding this event will also add its pre-built checklist to your main list.
                  </p>
                )}
              </div>

              {selectedEventType === CUSTOM_OPTION && (
                <div className="space-y-2">
                  <Label className="text-charcoal font-sans text-sm uppercase tracking-wider">Event Name</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="input-field h-12"
                    placeholder="Enter event name"
                    required
                    data-testid="event-name-input"
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label className="text-charcoal font-sans text-sm uppercase tracking-wider">Event Date</Label>
                <Input
                  type="date"
                  value={formData.event_date}
                  onChange={(e) => setFormData({ ...formData, event_date: e.target.value })}
                  className="input-field h-12"
                  data-testid="event-date-input"
                />
              </div>

              <div className="space-y-2" data-testid="event-budget-section">
                <div className="flex items-baseline justify-between gap-2">
                  <Label className="text-charcoal font-sans text-sm uppercase tracking-wider">Budget</Label>
                  {/* Display the slice as a % of the wedding's total budget so couples
                      see context as they drag (e.g. "Reception = 38% of total"). */}
                  {currentWedding?.total_budget > 0 && (
                    <span
                      className="text-[11px] font-mono tracking-wider text-muted"
                      data-testid="event-budget-pct"
                    >
                      {Math.round(((parseFloat(formData.budget) || 0) / currentWedding.total_budget) * 100)}% of total
                    </span>
                  )}
                </div>
                <Input
                  type="number"
                  value={formData.budget}
                  onChange={(e) => setFormData({ ...formData, budget: e.target.value })}
                  className="input-field h-12 font-mono"
                  placeholder="0"
                  min="0"
                  step="0.01"
                  data-testid="event-budget-input"
                />
                {/* Slider only renders when there's a wedding-level total to size
                    the max against. Step = 1% of total (rounded to nearest 50 for
                    natural-feeling drag); max = 80% of total (lets one event eat
                    most of the budget for outliers without going wild). */}
                {currentWedding?.total_budget > 0 && (() => {
                  const total = currentWedding.total_budget;
                  const max = Math.max(total * 0.8, parseFloat(formData.budget) || 0);
                  const step = Math.max(50, Math.round(total / 200));
                  return (
                    <div className="pt-2" data-testid="event-budget-slider-wrap">
                      <Slider
                        value={[parseFloat(formData.budget) || 0]}
                        onValueChange={(vals) =>
                          setFormData({ ...formData, budget: String(Math.round(vals[0])) })
                        }
                        min={0}
                        max={max}
                        step={step}
                        data-testid="event-budget-slider"
                      />
                      <div className="flex items-center justify-between text-[10px] font-mono tracking-wider text-muted mt-1.5 uppercase">
                        <span>0</span>
                        <span>Drag to rebalance</span>
                        <span>{formatCurrency(max)}</span>
                      </div>
                    </div>
                  );
                })()}
              </div>

              <div className="space-y-2">
                <Label className="text-charcoal font-sans text-sm uppercase tracking-wider">Description</Label>
                <Input
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="input-field h-12"
                  placeholder="Optional description"
                  data-testid="event-description-input"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="btn-secondary flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="btn-primary flex-1"
                  data-testid="save-event-btn"
                >
                  {editingEvent ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Auto-balance confirmation modal */}
        <Dialog open={showAutoBalance} onOpenChange={setShowAutoBalance}>
          <DialogContent className="bg-white max-w-md" data-testid="auto-balance-modal">
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl">Auto-balance events?</DialogTitle>
              <DialogDescription className="text-charcoal/70 leading-relaxed pt-2">
                Your event budgets currently total{' '}
                <span className="font-mono text-charcoal">{formatCurrency(totalEventBudgets)}</span>{' '}
                — over your{' '}
                <span className="font-mono text-charcoal">{formatCurrency(weddingTotal)}</span>{' '}
                wedding budget by{' '}
                <span className="font-mono text-terracotta">
                  {formatCurrency(totalEventBudgets - weddingTotal)}
                </span>.
                <br /><br />
                {(() => {
                  const lockedCount = (events || []).filter((e) => e.budget_locked).length;
                  if (lockedCount > 0) {
                    return (
                      <>
                        I'll keep{' '}
                        <span className="text-terracotta font-medium">{lockedCount} locked event{lockedCount === 1 ? '' : 's'}</span>{' '}
                        at their current budget and scale only the unlocked events to fit. The biggest unlocked event stays the biggest.
                      </>
                    );
                  }
                  return (
                    <>I'll scale every event proportionally so the total fits exactly. Each event keeps the same relative weight — the biggest stays the biggest. You can fine-tune any event afterward.</>
                  );
                })()}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="flex gap-2 mt-4">
              <Button
                type="button"
                onClick={() => setShowAutoBalance(false)}
                className="btn-secondary text-xs flex-1"
                disabled={autoBalancing}
                data-testid="auto-balance-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleAutoBalance}
                className="btn-primary text-xs flex-1"
                disabled={autoBalancing}
                data-testid="auto-balance-confirm-btn"
              >
                {autoBalancing ? 'Balancing…' : 'Balance now'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
